/*
 * @Description:
 * @Author: Luke Z
 * @Date: 2021-04-30 13:58:11
 * @LastEditors: Luke Z
 * @LastEditTime: 2021-05-18 12:43:30
 * @FilePath: /unifyxp-v2-store-portal/src/pages/ProductEditor/slice/common.ts
 */
import { createSelector } from '@reduxjs/toolkit';
import {
  getStateTarget as getTarget,
  collapseEditorWillUnMount,
  clearError,
  randomNumber,
} from 'src/tools/helper';
import { EnumReducers as parentEnumReducers } from 'src/store';
import {
  getState as _getState,
  getHasModified as _getHasModified,
  getCollapseStatus,
  getLoaded as _getLoaded,
  getLastModified as _getLastModified,
  getProductName as _getProductName,
} from 'src/tools/selectors/salesChannel';
import { remove } from 'lodash';
import { NEW } from 'src/tools/constant';
import { useSelector } from 'react-redux';

const legalReducers = (reducers) => {
  return [parentEnumReducers.storeProduct, ...reducers];
};

const reducerTarget = (state, reducers) => {
  return getTarget(state, legalReducers(reducers));
};

const getAllCollapse = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'collapseOpen']),
    (open) => {
      return open;
    },
  );
};

const getCollapse = (reducer, id) => {
  return getCollapseStatus(legalReducers([reducer]), id);
};

const getProductName = (reducer) => {
  return _getProductName(legalReducers([reducer, 'productInfo']));
};

const getState = (reducer) => {
  return _getState(legalReducers([reducer]));
};

const getLoaded = (reducer) => {
  return _getLoaded(legalReducers([reducer]));
};

const getHasModified = (reducer) => {
  return _getHasModified(legalReducers([reducer]));
};

const getLastModified = (reducer) => {
  return _getLastModified(legalReducers([reducer]));
};

const getName = ({ uuid, getName: _getName }) => {
  const _isNew = uuid === NEW;
  const name = useSelector(_getName());
  return _isNew ? 'Add New Product' : name;
};

const willUnmount = ({ state, defaultState }) => {
  collapseEditorWillUnMount({ state, defaultState });
};

const getUuid = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'uuid']),
    (state: any) => reducerTarget(state, [reducer, 'salesChannelUuid']),
    (uuid, salesChannelUuid) => {
      return { uuid: uuid || null, salesChannelUuid: salesChannelUuid || null };
    },
  );
};

const getProductInfo = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'productInfo']),
    (productInfo) => {
      return productInfo;
    },
  );
};

const getBrands = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'brands']),
    (brands) => {
      return brands;
    },
  );
};

const getTags = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'tags']),
    (tags) => {
      return tags;
    },
  );
};

const getThumbnailImage = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'thumbnailImage']),
    (thumbnailImage) => {
      return thumbnailImage;
    },
  );
};

const getPriceSettings = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'priceSettings']),
    (priceSettings) => {
      return priceSettings;
    },
  );
};

const getAdvancedPriceSettings = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'advancedPriceSettings']),
    (advancedPriceSettings) => {
      return advancedPriceSettings;
    },
  );
};

const getCurrencies = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'currencies']),
    (currencies) => {
      return currencies;
    },
  );
};

const getStock = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'stock']),
    (stock) => {
      return stock;
    },
  );
};

const getSalesChannelAvailableQuantity = (reducer) => {
  return createSelector(
    (state: any) =>
      reducerTarget(state, [reducer, 'salesChannelAvailableQuantity']),
    (salesChannelAvailableQuantity) => {
      return salesChannelAvailableQuantity;
    },
  );
};

const getInventoryUuid = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'inventoryUuid']),
    (inventoryUuid) => {
      return inventoryUuid;
    },
  );
};

const getStatus = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'status']),
    (status) => {
      return status;
    },
  );
};

const getProductType = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'productType']),
    (productType) => {
      return productType;
    },
  );
};

const getProductIsSold = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'hasBeenSold']),
    (isSold) => {
      return isSold;
    },
  );
};

const getLinkedSalesChannels = (reducer) => {
  return createSelector(
    (state: any) => reducerTarget(state, [reducer, 'linkedSalesChannels']),
    (linkedSalesChannels) => {
      return linkedSalesChannels;
    },
  );
};

function onBrandsAdd(state, action) {
  state.brands = [...state.brands, action.payload];
}

function onTagsChange(state, action) {
  state.tags = { ...state.tags, ...clearError(action.payload) };
  state.hasModified = true;
}

function onThumbnailImageChange(state, action) {
  state.thumbnailImage = action.payload;
  state.fileList = [];
  state.hasModified = true;
}

function onAdvancedPriceChange(state, action) {
  // state.priceSettings = { ...state.priceSettings, ...action.payload };
  const { isDelete, index, settings = {} } = action.payload;
  if (isDelete) {
    state.advancedPriceSettings.splice(index, 1);
  } else {
    state.advancedPriceSettings[index] = {
      ...(state.advancedPriceSettings[index] || {}),
      ...clearError(settings),
    };
  }
  state.hasModified = true;
}

function onPriceChange(state, action) {
  state.priceSettings = {
    ...state.priceSettings,
    ...clearError(action.payload),
  };
  state.hasModified = true;
}

function onAdvancedPriceAdd(state) {
  state.hasModified = true;
  state.advancedPriceSettings = [
    ...state.advancedPriceSettings,
    {
      key: randomNumber(),
      title: null,
      price: null,
      notes: null,
      currency: null,
    },
  ];
}

function onSalesChannelAvailableChange(state, action) {
  state.hasModified = true;
  const { index, quantity } = action.payload;
  state.salesChannelAvailableQuantity[index].quantity = quantity;
}

function onStockChange(state, action) {
  state.stock = { ...state.stock, ...clearError(action.payload) };
  state.hasModified = true;
}

function onLinkedSalesChannelsProductsChange(state, action) {
  const { uuid, products } = action.payload;
  state.linkedSalesChannels.forEach((i) => {
    if (i.uuid === uuid) {
      i.products = products || [];
    }
  });
}

function onLinkedSalesChannelsProductsDelete(state, action) {
  const { salesChannelUuid, productUuid } = action.payload;

  state.linkedSalesChannels.forEach((i) => {
    if (i.uuid === salesChannelUuid) {
      remove(i.products, (x: any) => {
        return x.uuid === productUuid;
      });
    }
  });
}

// function onSubCollapseOpen(state, action) {
//   const ids = action.payload || {};
//   Object.keys(ids).forEach((i) => {
//     state.subCollapseOpen[i] = ids[i];
//   });
// }

export {
  getLinkedSalesChannels,
  onBrandsAdd,
  onTagsChange,
  getProductType,
  getStatus,
  getBrands,
  reducerTarget,
  getCollapse,
  getAllCollapse,
  getState,
  getHasModified,
  getName,
  getUuid,
  willUnmount,
  getLoaded,
  getProductInfo,
  getLastModified,
  getTags,
  onThumbnailImageChange,
  getThumbnailImage,
  onAdvancedPriceChange,
  onPriceChange,
  onAdvancedPriceAdd,
  getAdvancedPriceSettings,
  getPriceSettings,
  getCurrencies,
  onSalesChannelAvailableChange,
  onStockChange,
  getStock,
  getSalesChannelAvailableQuantity,
  getInventoryUuid,
  onLinkedSalesChannelsProductsChange,
  getProductName,
  onLinkedSalesChannelsProductsDelete,
  getProductIsSold,
  //onSubCollapseOpen,
};
